import { useMutation } from "@apollo/client";
import { useMemo, useState } from "react";
import AppButton from "../../../../base/AppButton.component";
import { useVerification } from "../../../../../context/Verification.context";
import { MANAGE_WARRANTY_RESPONSE } from "../../../../../graphql/mutation/mutation";
import {
  buildChartData,
  DEFAULT_ANNUAL_MILES,
  DEFAULT_YEARS_OWNED,
} from "../../../../../utils/warrantyChart.utils";
import Theme from "../../../../../utils/Theme";
import WarrantyChartView from "../../../warranty/WarrantyChartView.component";

const WarrantyChart = ({
  coverages = [],
  exclusions = [],
  vehicleTypeId,
  onNext,
}) => {
  const verification = useVerification();
  const isSpanish = Boolean(verification?.aiData?.spanish);

  const [annualMiles, setAnnualMiles] = useState(DEFAULT_ANNUAL_MILES);
  const [yearsOwned, setYearsOwned] = useState(DEFAULT_YEARS_OWNED);
  const [saving, setSaving] = useState(false);

  const [manageWarrantyResponse] = useMutation(MANAGE_WARRANTY_RESPONSE);

  const chartData = useMemo(
    () => buildChartData(coverages, annualMiles, yearsOwned, isSpanish),
    [coverages, annualMiles, yearsOwned, isSpanish],
  );

  const handleNext = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const dealershipId = Number(verification?.dealershipId);
      const userId = Number(verification?.customer?.userId);
      const employeeId = Number(verification?.employeeId);
      const safeEmployeeId = Number.isFinite(employeeId) ? employeeId : null;
      const safeUserId = Number.isFinite(userId) ? userId : null;
      const createdBy = safeEmployeeId || safeUserId;

      await manageWarrantyResponse({
        variables: {
          warrantyResponseInput: {
            dealership_id: dealershipId,
            user_id: safeUserId,
            employee_user_id: safeEmployeeId,
            warranty_vehicle_id: vehicleTypeId,
            annual_mileage: annualMiles,
            years_owned: yearsOwned,
            created_by: createdBy,
            updated_by: createdBy,
            items: chartData.map((bar) => ({
              warranty_coverage_id: bar.coverageId,
              covered_months: bar.covered,
              uncovered_months: bar.uncovered,
            })),
          },
        },
      });
    } catch (err) {
      console.error("Failed to save warranty response", err);
    } finally {
      setSaving(false);
      onNext?.();
    }
  };

  return (
    <WarrantyChartView
      coverages={coverages}
      exclusions={exclusions}
      annualMiles={annualMiles}
      yearsOwned={yearsOwned}
      onAnnualMilesChange={setAnnualMiles}
      onYearsOwnedChange={setYearsOwned}
      isSpanish={isSpanish}
      readOnly={false}
      animate
      headerAction={
        <AppButton
          buttonText={isSpanish ? "Siguiente >" : "Next >"}
          variant="text"
          size="small"
          clickHandler={handleNext}
          loading={saving}
          sx={{
            minWidth: 72,
            color: Theme.palette.primary.main,
            fontWeight: 700,
            flexShrink: 0,
          }}
        />
      }
    />
  );
};

export default WarrantyChart;
